/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import StorageService from "./StorageService";

// FileSystem Access API types
interface FileSystemDirectoryHandle {
  name: string;
  kind: "directory";
  getFileHandle: (
    name: string,
    options?: { create?: boolean }
  ) => Promise<FileSystemFileHandle>;
  queryPermission: (descriptor: {
    mode: "read" | "readwrite";
  }) => Promise<PermissionState>;
  requestPermission: (descriptor: {
    mode: "read" | "readwrite";
  }) => Promise<PermissionState>;
}

interface FileSystemFileHandle {
  name: string;
  kind: "file";
  createWritable: () => Promise<FileSystemWritableFileStream>;
}

interface FileSystemWritableFileStream {
  write: (data: string | BufferSource | Blob) => Promise<void>;
  close: () => Promise<void>;
}

// ServiceWorker PeriodicSync types
interface PeriodicSyncManager {
  register: (tag: string, options?: { minInterval: number }) => Promise<void>;
}

interface ServiceWorkerRegistrationWithPeriodicSync
  extends ServiceWorkerRegistration {
  periodicSync?: PeriodicSyncManager;
}

// Backup configuration interface
export interface BackupConfig {
  frequency: "hourly" | "daily" | "weekly" | "monthly" | "manual";
  maxBackups: number;
  autoDownload: boolean;
  lastBackup: string | null;
  useFileSystem: boolean;
  fileSystemHandle: {
    name: string;
  } | null;
}

// Backup entry interface
export interface BackupEntry {
  id: string;
  timestamp: string;
  appVersion: string;
  size: number;
  data?: {
    scanHistory: Record<string, any[]>;
    operators: any[];
    appState: any;
    managerData: any;
  };
}

// Default configuration
const DEFAULT_CONFIG: BackupConfig = {
  frequency: "daily",
  maxBackups: 10,
  autoDownload: false,
  lastBackup: null,
  useFileSystem: false,
  fileSystemHandle: null,
};

class BackupService {
  private serviceWorkerRegistration: ServiceWorkerRegistrationWithPeriodicSync | null =
    null;
  private isInitialized: boolean = false;
  private config: BackupConfig = DEFAULT_CONFIG;
  private backups: BackupEntry[] = [];
  private fileSystemDirectoryHandle: FileSystemDirectoryHandle | null = null;

  // Initialize the backup service
  async initialize(): Promise<boolean> {
    if (this.isInitialized) return true;

    try {
      // Register the service worker
      if ("serviceWorker" in navigator) {
        this.serviceWorkerRegistration = await navigator.serviceWorker.register(
          "/backup-service-worker.js"
        );
        console.log("Backup Service Worker registered");

        // Set up message listener
        navigator.serviceWorker.addEventListener(
          "message",
          this.handleServiceWorkerMessage
        );

        // Request config from service worker
        this.getConfig();

        // Register for periodic sync if available
        if ("periodicSync" in this.serviceWorkerRegistration) {
          try {
            await (this.serviceWorkerRegistration as any).periodicSync.register(
              "backup",
              {
                minInterval: 24 * 60 * 60 * 1000, // 24 hours
              }
            );
            console.log("Periodic Sync registered");
          } catch (error) {
            console.warn("Periodic Sync registration failed:", error);
          }
        } else {
          console.log("Periodic Sync not supported, using fallback");
          this.setupFallbackScheduling();
        }

        this.isInitialized = true;
        return true;
      } else {
        console.warn("Service Workers not supported");
        return false;
      }
    } catch (error) {
      console.error("Error initializing backup service:", error);
      return false;
    }
  }

  // Handle messages from the service worker
  private handleServiceWorkerMessage = (event: MessageEvent) => {
    const { command, data } = event.data;

    switch (command) {
      case "REQUEST_BACKUP_DATA":
        this.sendBackupData();
        break;
      case "BACKUP_CREATED":
        console.log("Backup created:", data);
        this.getBackups();
        this.updateLastBackupTime();
        break;
      case "BACKUPS_LIST":
        this.backups = data;
        break;
      case "CONFIG_DATA":
        this.config = data;
        this.restoreFileSystemAccess();
        break;
      case "RESTORE_DATA":
        this.handleRestore(data);
        break;
      case "BACKUP_ERROR":
        console.error("Backup error:", data.error);
        break;
    }
  };

  // Send current app data to service worker for backup
  private async sendBackupData() {
    // Get complete data from localStorage
    const completeData = this.getCompleteBackupData();

    // Send to service worker
    this.sendToServiceWorker("CREATE_BACKUP", completeData);

    // If auto download is enabled, trigger a download
    if (this.config.autoDownload) {
      this.downloadBackup(completeData);
    }

    // If file system access is enabled, save to file system
    if (this.config.useFileSystem && this.fileSystemDirectoryHandle) {
      await this.saveToFileSystem(completeData);
    }
  }

  // Get complete backup data
  private getCompleteBackupData() {
    // Get data from StorageService and raw localStorage
    const backupData = {
      appVersion: "1.0.0",
      timestamp: new Date().toISOString(),
      data: {
        // Get the specific app data using our storage service
        scanHistory: StorageService.getScanHistory(),
        operators: StorageService.getOperators(),
        appState: StorageService.getAppState(),
        managerData: StorageService.getManagerData(),

        // Also include all raw localStorage data for complete backup
        localStorage: this.getLocalStorageData(),
      },
    };

    return backupData;
  }

  // Get all localStorage data
  private getLocalStorageData(): Record<string, string> {
    const data: Record<string, string> = {};

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        data[key] = localStorage.getItem(key) || "";
      }
    }

    return data;
  }

  // Send a message to the service worker
  private sendToServiceWorker(command: string, data?: any) {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        command,
        data,
      });
    }
  }

  // Get configuration from service worker
  getConfig() {
    this.sendToServiceWorker("GET_CONFIG");
  }

  // Set configuration
  async setConfig(config: Partial<BackupConfig>): Promise<boolean> {
    try {
      const newConfig = { ...this.config, ...config };
      this.config = newConfig;
      this.sendToServiceWorker("SET_CONFIG", newConfig);

      // Update scheduling based on new config
      if (newConfig.frequency !== "manual") {
        this.setupFallbackScheduling();
      }

      return true;
    } catch (error) {
      console.error("Error setting config:", error);
      return false;
    }
  }

  // Get all backups
  getBackups() {
    this.sendToServiceWorker("GET_BACKUPS");
  }

  // Create a backup manually
  async createBackup(): Promise<boolean> {
    try {
      await this.sendBackupData();
      return true;
    } catch (error) {
      console.error("Error creating backup:", error);
      return false;
    }
  }

  // Restore a backup
  async restoreBackup(backupId: string): Promise<boolean> {
    try {
      this.sendToServiceWorker("RESTORE_BACKUP", { id: backupId });
      return true;
    } catch (error) {
      console.error("Error restoring backup:", error);
      return false;
    }
  }

  // Delete a backup
  async deleteBackup(backupId: string): Promise<boolean> {
    try {
      this.sendToServiceWorker("DELETE_BACKUP", { id: backupId });
      return true;
    } catch (error) {
      console.error("Error deleting backup:", error);
      return false;
    }
  }

  // Handle restore from service worker
  private handleRestore(backupData: BackupEntry) {
    try {
      if (!backupData.data) {
        throw new Error("Backup data is empty");
      }

      // Restore the specific app data structures
      if (backupData.data.scanHistory) {
        this.restoreScanHistory(backupData.data.scanHistory);
      }

      if (backupData.data.operators) {
        this.restoreOperators(backupData.data.operators);
      }

      if (backupData.data.appState) {
        this.restoreAppState(backupData.data.appState);
      }

      if (backupData.data.managerData) {
        this.restoreManagerData(backupData.data.managerData);
      }

      // Then restore raw localStorage data if available
      if (backupData.data.localStorage) {
        this.restoreLocalStorage(backupData.data.localStorage);
      }

      console.log("Data restored from backup");

      // Automatically reload the page after successful restoration
      this.reloadWithDelay();
    } catch (error) {
      console.error("Error handling restore:", error);
      throw error;
    }
  }

  // Restore localStorage data
  private restoreLocalStorage(data: Record<string, string>) {
    // Clear keys not in the backup (optional, could be commented out if you want to merge instead)
    // for (let i = localStorage.length - 1; i >= 0; i--) {
    //   const key = localStorage.key(i);
    //   if (key && !(key in data)) {
    //     localStorage.removeItem(key);
    //   }
    // }

    // Restore all keys from backup
    Object.entries(data).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  }

  // Helper methods for specific data restoration
  private restoreScanHistory(scanHistory: Record<string, any[]>) {
    // Clear existing scan history keys
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith("lumibot:scanHistory")) {
        localStorage.removeItem(key);
      }
    });

    // Use StorageService to set the scan history
    localStorage.setItem(
      StorageService.STORAGE_KEYS.SCAN_HISTORY,
      JSON.stringify(scanHistory)
    );
  }

  private restoreOperators(operators: any[]) {
    localStorage.setItem(
      StorageService.STORAGE_KEYS.OPERATORS,
      JSON.stringify(operators)
    );
  }

  private restoreAppState(appState: any) {
    localStorage.setItem(
      StorageService.STORAGE_KEYS.APP_STATE,
      JSON.stringify(appState)
    );
  }

  private restoreManagerData(managerData: any) {
    localStorage.setItem(
      StorageService.STORAGE_KEYS.MANAGER,
      JSON.stringify(managerData)
    );
  }

  // Setup fallback scheduling for browsers without periodic sync
  private setupFallbackScheduling() {
    // Calculate next backup time based on frequency
    const calculateNextBackup = () => {
      const now = new Date();
      const lastBackup = this.config.lastBackup
        ? new Date(this.config.lastBackup)
        : null;

      if (!lastBackup) return 1; // Backup immediately if no previous backup

      switch (this.config.frequency) {
        case "hourly":
          // Next hour, same minute and second
          // eslint-disable-next-line no-case-declarations
          const nextHour = new Date(lastBackup);
          nextHour.setHours(nextHour.getHours() + 1);
          return Math.max(1, nextHour.getTime() - now.getTime());

        case "daily":
          // Next day, same time
          // eslint-disable-next-line no-case-declarations
          const nextDay = new Date(lastBackup);
          nextDay.setDate(nextDay.getDate() + 1);
          return Math.max(1, nextDay.getTime() - now.getTime());

        case "weekly":
          // Next week, same day and time
          // eslint-disable-next-line no-case-declarations
          const nextWeek = new Date(lastBackup);
          nextWeek.setDate(nextWeek.getDate() + 7);
          return Math.max(1, nextWeek.getTime() - now.getTime());

        case "monthly":
          // Next month, same day and time
          // eslint-disable-next-line no-case-declarations
          const nextMonth = new Date(lastBackup);
          nextMonth.setMonth(nextMonth.getMonth() + 1);
          return Math.max(1, nextMonth.getTime() - now.getTime());

        default:
          return -1; // Don't schedule if manual
      }
    };

    const scheduleNextBackup = () => {
      const delay = calculateNextBackup();

      if (delay > 0) {
        console.log(
          `Scheduling next backup in ${Math.floor(
            delay / (1000 * 60 * 60)
          )} hours`
        );
        setTimeout(() => {
          this.createBackup().then(() => {
            scheduleNextBackup(); // Schedule the next one after success
          });
        }, delay);
      }
    };

    // Start scheduling
    scheduleNextBackup();

    // Also check on visibility change
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        const lastBackupTime = this.config.lastBackup
          ? new Date(this.config.lastBackup).getTime()
          : 0;
        const now = Date.now();

        // If it's been too long since the last backup, create one
        if (
          lastBackupTime === 0 ||
          now - lastBackupTime > 24 * 60 * 60 * 1000
        ) {
          this.createBackup();
        }
      }
    });
  }

  // Update the last backup time
  private updateLastBackupTime() {
    const now = new Date().toISOString();
    this.setConfig({ lastBackup: now });
  }

  // Request file system access
  async requestFileSystemAccess(): Promise<boolean> {
    if (!("showDirectoryPicker" in window)) {
      console.warn("File System Access API not supported");
      return false;
    }

    try {
      // Using the FileSystem Access API
      const dirHandle = (await (window as any).showDirectoryPicker({
        id: "backupDirectory",
        mode: "readwrite",
        startIn: "documents",
      })) as FileSystemDirectoryHandle;

      // Verify we have permission
      const permissionState = await dirHandle.queryPermission({
        mode: "readwrite",
      });

      if (permissionState !== "granted") {
        const newPermission = await dirHandle.requestPermission({
          mode: "readwrite",
        });
        if (newPermission !== "granted") {
          return false;
        }
      }

      this.fileSystemDirectoryHandle = dirHandle;

      // Update config
      this.setConfig({
        useFileSystem: true,
        fileSystemHandle: {
          name: dirHandle.name,
        },
      });

      return true;
    } catch (error) {
      console.error("Error requesting file system access:", error);
      return false;
    }
  }

  // Restore file system access
  private async restoreFileSystemAccess() {
    if (this.config.useFileSystem && "fileSystemHandle" in this.config) {
      if ("getFileHandle" in navigator.storage) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const handle = await navigator.storage.getDirectory();
          if (handle) {
            this.fileSystemDirectoryHandle = handle;
          }
        } catch (error) {
          console.warn("Could not restore file system access:", error);
          this.setConfig({ useFileSystem: false });
        }
      }
    }
  }

  // Save backup to file system
  private async saveToFileSystem(backupData: any): Promise<boolean> {
    if (!this.fileSystemDirectoryHandle) {
      return false;
    }

    try {
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `lumibot_backup_${timestamp}.json`;

      const fileHandle = await this.fileSystemDirectoryHandle.getFileHandle(
        filename,
        { create: true }
      );
      const writable = await fileHandle.createWritable();
      await writable.write(JSON.stringify(backupData, null, 2));
      await writable.close();

      console.log("Backup saved to file system:", filename);
      return true;
    } catch (error) {
      console.error("Error saving to file system:", error);
      return false;
    }
  }

  // Download backup as file - Updated to use complete data
  downloadBackup(backupData: any) {
    // Ensure we have complete data
    const dataToDownload = backupData.data
      ? backupData
      : this.getCompleteBackupData();

    const backupStr = JSON.stringify(dataToDownload, null, 2);
    const blob = new Blob([backupStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `lumibot_backup_${timestamp}.json`;

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();

    // Clean up
    setTimeout(() => {
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 100);
  }

  // Get available backups
  getAvailableBackups(): BackupEntry[] {
    return this.backups;
  }

  // Get current config
  getCurrentConfig(): BackupConfig {
    return this.config;
  }

  // Import backup from file - Updated to restore localStorage
  async importBackupFromFile(file: File): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const backupData = JSON.parse(event.target?.result as string);

          // Validate the backup data
          if (!backupData || !backupData.data) {
            reject(new Error("Invalid backup file"));
            return;
          }

          // First restore directly to localStorage
          if (backupData.data.localStorage) {
            this.restoreLocalStorage(backupData.data.localStorage);
          } else {
            // Older backup format might not have localStorage data
            // Restore individual components
            this.handleRestore(backupData);

            // Since handleRestore already calls reloadWithDelay, return early
            // to avoid double reload
            resolve(true);
            return;
          }

          // Then also store in IndexedDB for history
          this.sendToServiceWorker("CREATE_BACKUP", backupData);

          // Automatically reload the page after successful import
          this.reloadWithDelay();

          resolve(true);
        } catch (error) {
          console.error("Error parsing backup file:", error);
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error("Error reading backup file"));
      };

      reader.readAsText(file);
    });
  }

  // Helper method to reload the page with a slight delay
  private reloadWithDelay(delayMs: number = 1000): void {
    console.log(
      `Application will reload in ${delayMs}ms to apply restored data...`
    );
    setTimeout(() => {
      window.location.reload();
    }, delayMs);
  }
}

export default new BackupService();
